import * as React from 'react';
import { isRouteErrorResponse, useRouteError, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SearchX, TriangleAlert } from 'lucide-react';

/**
 * Router-level error boundary (the `errorElement` on the root route). Without
 * it, a bad URL or a render/loader error showed react-router's raw
 * "Unexpected Application Error!" developer page. Distinguishes a 404 (wrong
 * address — calm, navigational) from a crash (apologetic, recoverable).
 * Sentry render crashes below <Root> are still caught by the outer
 * Sentry.ErrorBoundary in main.tsx; this catches what the router intercepts
 * first.
 */
export default function RouteErrorPage() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
          {notFound ? (
            <SearchX className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          ) : (
            <TriangleAlert className="h-6 w-6 text-warning" aria-hidden="true" />
          )}
        </div>
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
          {notFound ? 'Page not found' : 'Something went wrong'}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {notFound
            ? "This page doesn't exist. The address may be mistyped, or the link is out of date."
            : 'An unexpected error interrupted this page. Your data is safe. Try reloading, or head back to the dashboard.'}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button asChild>
            <Link to="/admin/dashboard">Go to Dashboard</Link>
          </Button>
          {!notFound && (
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
